import AddRoomButton from 'components/Common/AddRoomButton'
import noop from 'lib/function/noop'
import { forwardRef, memo, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { formatSaleUnitLabel } from 'hooks/useSaleUnitFormatting'
import type { AdultLabel } from 'lib/offer/occupancyUtils'
import { generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import config from 'constants/config'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import OccupancyItemVertical from './OccupancyItemVertical'

interface Props extends Utils.PropsWithDataAttributes {
  occupancies: Array<App.Occupants | App.RoomOccupants>
  maxRooms?: number
  capacities?: Array<App.RoomCapacity>
  maxChildAge?: number
  maxInfantAge?: number
  maxTeenagerAge?: number
  saleUnit?: string
  onChange?: (occupancies: Array<App.Occupants | App.RoomOccupants>) => void
  name?: string
  className?: string
  childrenAgeLabel?: string
  enableMultiRoom?: boolean
  adultLabelType?: AdultLabel
  showChildren?: boolean
  offerType?: App.OfferType
  showChildrenSelectLabel?: boolean
}

const OccupancyPickerVertical = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    occupancies,
    capacities,
    maxChildAge,
    maxInfantAge,
    maxTeenagerAge,
    saleUnit = 'room',
    onChange = noop,
    maxRooms,
    name = 'occupants',
    childrenAgeLabel,
    enableMultiRoom,
    adultLabelType,
    showChildren,
    offerType,
    showChildrenSelectLabel,
    ...rest
  } = props

  const intl = useIntl()

  const resolvedChildrenAgeLabel = childrenAgeLabel ?? intl.formatMessage({
    id: 'GBbQFyyy2xTvJgjpJfV6',
    defaultMessage: 'Age at time of check-in',
    description: 'Default label for children age selection in occupancy picker',
  })

  const isInstanceOfRoomOccupants = useMemo(() => occupancies.length && ('roomId' in occupancies[0]), [occupancies])

  const onAddAnotherRoom = useCallback(() => {
    const newRoom = isInstanceOfRoomOccupants ? generateOfferPageRoom(config.search.defaultOccupants) : { ...config.search.defaultOccupants }
    const newOccupancies = [...occupancies, newRoom]
    onChange(newOccupancies)
  }, [occupancies, onChange, isInstanceOfRoomOccupants])

  const onItemChange = useCallback((index: number, newOccupancy: App.Occupants) => {
    const newOccupancies = [...occupancies]
    newOccupancies[index] = { ...newOccupancies[index], ...newOccupancy }
    onChange(newOccupancies)
  }, [occupancies, onChange])

  return (<VerticalSpacer {...rest} gap={20} ref={ref}>
    {occupancies.map((occupants, index) => <OccupancyItemVertical
      key={index}
      itemId={index}
      occupancy={occupants}
      title={intl.formatMessage({
        id: '9YaRpLF5qOO-1QkeJbK_',
        defaultMessage: '{saleUnit} {index}',
        description: 'Title for room/unit in occupancy picker, e.g. Room 1',
      }, { saleUnit: formatSaleUnitLabel(intl, saleUnit) ?? saleUnit, index: index + 1 })}
      maxChildAge={maxChildAge}
      maxInfantAge={maxInfantAge}
      maxTeenagerAge={maxTeenagerAge}
      onChange={onItemChange}
      capacities={capacities}
      childrenAgeLabel={resolvedChildrenAgeLabel}
      name={name}
      adultLabelType={adultLabelType}
      showChildren={showChildren}
      offerType={offerType}
      showChildrenSelectLabel={showChildrenSelectLabel}
    />)}
    {enableMultiRoom && <div>
      <AddRoomButton
        maxRooms={maxRooms}
        saleUnit={saleUnit}
        roomsCount={occupancies.length}
        onClick={onAddAnotherRoom}
      />
    </div>}
  </VerticalSpacer>
  )
})

OccupancyPickerVertical.displayName = 'OccupancyPicker'

export default memo(OccupancyPickerVertical)
